import { Component, ElementRef, ViewChild } from "@angular/core";
import _ from "lodash";
import { FeatureKind } from "app/model/entities/entity-model";
import { DialogBaseComponent, DialogParams } from "app/shared/dialog-base.component";
import { DomainService } from "app/shared/domain.service";
import { EntityFns } from "app/shared/entity-fns";
import { UtilFns } from "app/shared/util-fns";
import { DynamicDialogConfig, DynamicDialogRef } from "primeng/dynamicdialog";

export interface EditFeatureKindDialogParams extends DialogParams {
  /** Absent means add one. */
  featureKind?: FeatureKind;
}

/** The id reserved for 'genuinely neither', kept at the end of the list rather than in sequence. */
const SENTINEL_ID = 99;

@Component({
  templateUrl: "./edit-feature-kind-dialog.component.html",
  standalone: false,
})
export class EditFeatureKindDialogComponent extends DialogBaseComponent<EditFeatureKindDialogParams> {
  @ViewChild("firstInput") firstInput: ElementRef<HTMLInputElement>;

  params: EditFeatureKindDialogParams;
  featureKind: FeatureKind;
  isAdding: boolean;

  constructor(
    public config: DynamicDialogConfig<EditFeatureKindDialogParams>,
    public dialogRef: DynamicDialogRef,
    public domainService: DomainService
  ) {
    super(config, dialogRef, domainService);

    this.prepare();
  }

  private async prepare() {
    this.featureKind = this.params.featureKind;
    if (this.featureKind == null) {
      this.isAdding = true;
      this.featureKind = await this.createNewFeatureKindAsUnchanged();
    }

    this.config.header = this.isAdding ? "Add Feature Type" : "Edit Feature Type";
    this.isPageReady = true;

    await UtilFns.wait(1);
    this.firstInput?.nativeElement?.focus();
  }

  /**
   * FeatureKind.Id is ValueGeneratedNever - the rows mirror an enum, so the database issues no
   * id and one has to be chosen here.
   *
   * Chosen as the next free number BELOW the sentinel, so a new type lands at 4 rather than at
   * 100. 99 means 'Other' and is deliberately parked at the end of the list; counting from the
   * highest id would drag every later addition past it and make the list read in a nonsense
   * order wherever it is sorted by id.
   */
  private async createNewFeatureKindAsUnchanged() {
    const all = await this.dbQueryService.getAll(FeatureKind);
    const highest = _.max(all.map((k) => k.id).filter((id) => id < SENTINEL_ID)) ?? 0;

    const featureKind = this.uow.createEntity(FeatureKind, { id: highest + 1 });
    featureKind.entityAspect.setUnchanged();
    featureKind.entityAspect.validateEntity();
    return featureKind;
  }

  async ok() {
    const ok = await this.save();
    if (!ok) return;
    this.dialogRef.close(true);
  }

  async save() {
    if (this.isAdding) {
      this.featureKind.entityAspect.setAdded();
    }
    this.featureKind.name = this.featureKind.name?.trim();

    const isValid = await this.validate();
    if (!isValid) return false;

    try {
      // Only this row. The feature types are a lookup the rest of the app holds in the same
      // entity manager, so an unscoped save would carry whatever another screen left pending.
      await this.dbSaveService.saveSelectedChanges([this.featureKind]);
      this.toastr.success("The system saved the feature type.");
      return true;
    } catch (e) {
      return false;
    }
  }

  async validate() {
    if (!this.featureKind.name) {
      await this.dialogService.showOkMessage("Name Required", "Type a name for the feature type.");
      return false;
    }

    // Unique on add AND on edit. getMatchingEntity compares against the other rows only, so an
    // edit that leaves the name alone does not report the row as a duplicate of itself.
    const matchingEntity = await this.dbQueryService.getMatchingEntity(FeatureKind, this.featureKind, ["name"]);
    if (matchingEntity != null) {
      await this.dialogService.showOkMessage(
        "Duplicate",
        `A feature type called '${this.featureKind.name}' already exists. Type a different name.`
      );
      return false;
    }

    const ea = this.featureKind.entityAspect;
    if (!ea.validateEntity()) {
      const vErrors = ea.getValidationErrors();
      const errMsg = vErrors.map((ve) => ve.errorMessage).join("<br>");
      await this.dialogService.showOkMessage("Validation Error(s)", errMsg);
      return false;
    }
    return true;
  }

  async cancel() {
    this.isPageReady = false;
    if (this.isAdding) {
      this.featureKind.entityAspect.setDetached();
    } else {
      EntityFns.rejectOrDetach(this.featureKind.entityAspect);
    }
    this.dialogRef.close(false);
  }
}
